import { onlineVisitFactorUrl } from '@/common/utils/onlineVisitFactorUrl';
import { useSearch } from '@/common/apis/services/search/search';
import Button from '@/common/components/atom/button';
import Modal from '@/common/components/atom/modal';
import Skeleton from '@/common/components/atom/skeleton';
import Text from '@/common/components/atom/text';
import { SubstituteDoctorSuggestion } from '@/modules/samanBooking/SubstituteDoctorButton';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

const normalizedSlug = (value: string) => {
  try { return decodeURIComponent(value).replace(/\/$/, ''); }
  catch { return value.replace(/\/$/, ''); }
};

export function selectOnlineVisitAlternatives(doctors: any[], originalSlug: string, limit = 3) {
  const seen = new Set<string>();
  return doctors.filter(doctor => {
    if (doctor?.type !== 'doctor' || typeof doctor.url !== 'string') return false;
    let urlSlug: string;
    try { urlSlug = new URL(doctor.url, 'https://www.paziresh24.com').pathname.match(/^\/dr\/([^/]+)\/?$/)?.[1] ?? ''; }
    catch { return false; }
    const slug = normalizedSlug(urlSlug);
    if (!slug || slug === normalizedSlug(originalSlug) || normalizedSlug(doctor.slug ?? '') === normalizedSlug(originalSlug) || seen.has(slug)) return false;
    const online = doctor.actions?.some((action: any) => action.title === 'ویزیت آنلاین' && action.disabled !== true && action.is_disabled !== true);
    if (!online) return false;
    seen.add(slug);
    return true;
  }).slice(0, limit);
}

interface Props {
  reason: 'doctor' | 'service';
  doctorSlug: string;
  category: string;
  categoryLoading: boolean;
  onRetryCategory: () => void;
}

export default function OnlineVisitUnavailable({ reason, doctorSlug, category, categoryLoading, onRetryCategory }: Props) {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setOpen(true), 300);
    return () => clearTimeout(timer);
  }, [open]);
  const router = useRouter();
  const message = reason === 'doctor' ? 'نوبت‌دهی ویزیت آنلاین این پزشک غیرفعال است.' : 'این خدمت ویزیت آنلاین در دسترس نیست.';
  const search = useSearch({ route: 'ir', query: { turn_type: 'consult', text: category } }, {
    enabled: open && !!category.trim(),
    keepPreviousData: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    staleTime: 0,
    retry: 1,
  });
  const doctors = selectOnlineVisitAlternatives(search.data?.search?.result ?? [], doctorSlug);
  const loading = categoryLoading || (!!category && (search.isLoading || search.isFetching));

  return <>
    <Modal name="online-visit-unavailable" bindHistory={false} isOpen={open} onClose={() => setOpen(false)} title="ویزیت آنلاین در دسترس نیست" bodyClassName="!p-5">
      <div className="flex flex-col gap-5">
        <Text className="text-slate-600">{message} می‌توانید پزشک دیگری در همین تخصص انتخاب کنید.</Text>
        {loading ? <div className="flex gap-3 overflow-hidden" aria-label="در حال دریافت پزشکان مرتبط"><Skeleton w="15rem" h="14rem" rounded="lg" className="shrink-0" /><Skeleton w="15rem" h="14rem" rounded="lg" className="shrink-0" /></div> :
          !category || search.isError ? <div className="flex flex-col gap-3"><Text>{!category ? 'تخصص پزشک دریافت نشد.' : 'پیشنهاد پزشکان دریافت نشد.'}</Text><Button variant="secondary" onClick={() => !category ? onRetryCategory() : void search.refetch()}>تلاش دوباره</Button></div> :
          <>
            {doctors.length ? <SubstituteDoctorSuggestion doctor={doctors[0]} groupName={category} hideBanner onSelect={() => void router.push(onlineVisitFactorUrl(doctors[0]))} /> : <Text className="text-slate-600">در حال حاضر پزشک آنلاین مرتبطی پیدا نکردیم.</Text>}
            <Button variant="secondary" onClick={() => router.push(`/s/ir/?turn_type=consult&text=${encodeURIComponent(category)}`)}>پزشکان آنلاین بیشتر</Button>
          </>}
      </div>
    </Modal>
  </>;
}
